"use client"

import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@baluu/ui/lib/utils"

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

type PopoverContentProps = React.ComponentProps<typeof PopoverPrimitive.Content> & {
  /** Container element for the portal. If null, renders inline without portal (for Shadow DOM) */
  container?: HTMLElement | null;
};

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  container,
  onPointerDownOutside,
  ...props
}: PopoverContentProps) {
  // Ref to the content element for Shadow DOM click detection
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Capture container value for use in callback
  const isInlineMode = container === null;

  const content = (
    <PopoverPrimitive.Content
      ref={contentRef}
      data-slot="popover-content"
      align={align}
      sideOffset={sideOffset}
      onPointerDownOutside={(e) => {
        // FIX: In Shadow DOM (container={null}), Radix incorrectly detects clicks on items as "outside"
        // because event.target returns the shadow host instead of the actual element.
        // Use composedPath() to check if the click is actually inside the content.
        if (isInlineMode && contentRef.current) {
          const originalEvent = e.nativeEvent ?? e.detail?.originalEvent;
          const path = originalEvent?.composedPath?.() || [];
          const isInsideContent = path.some((el: EventTarget) => el === contentRef.current);
          if (isInsideContent) {
            e.preventDefault();
            return;
          }
        }
        // Call the original handler if provided
        onPointerDownOutside?.(e);
      }}
      className={cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-72 origin-(--radix-popover-content-transform-origin) rounded-md border p-4 shadow-md outline-hidden",
        className
      )}
      {...props}
    />
  );

  // If container is explicitly null, render inline (no portal) - useful for Shadow DOM
  if (container === null) {
    return content;
  }

  // Otherwise use portal (default behavior, or with custom container)
  return (
    <PopoverPrimitive.Portal container={container}>
      {content}
    </PopoverPrimitive.Portal>
  );
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor }
